"use client";
import {
  AlertTriangle,
  Check,
  Gauge,
} from "lucide-react";
import {
  type ScanReport,
} from "../../../lib/types";


export function CoverageView({ report }: { report: ScanReport }) {
  const coverage = report.coverage;
  const benchmark = report.benchmark;
  const classCoverage = coverage.totalClassEntries
    ? Math.round((coverage.parsedClasses / coverage.totalClassEntries) * 1000) /
      10
    : 100;
  return (
    <div className="coverage-workspace">
      <section className="coverage-hero panel">
        <div>
          <Gauge size={30} />
        </div>
        <span>
          <div className="section-kicker">HONEST RELEASE MEASUREMENT</div>
          <h2>{benchmark.expectedOutcomesPassed}/{benchmark.totalCases} regression checks pass</h2>
          <p>{benchmark.disclaimer}</p>
        </span>
      </section>
      <section className="coverage-grid">
        <article className="panel">
          <span>AUTOMATED CHECKS</span>
          <strong>{benchmark.totalCases}</strong>
          <small>{benchmark.version}</small>
        </article>
        <article className="panel">
          <span>MALICIOUS CASES</span>
          <strong>{benchmark.maliciousCases}</strong>
          <small>family, behavior, and payload fixtures</small>
        </article>
        <article className="panel">
          <span>CLEAN CONTROLS</span>
          <strong>{benchmark.falsePositiveControlsPassed}/{benchmark.cleanControls}</strong>
          <small>expected to remain unlabelled</small>
        </article>
        <article className="panel">
          <span>MUTATIONS</span>
          <strong>{benchmark.mutationCases}</strong>
          <small>renamed, repacked, nested, or encoded</small>
        </article>
      </section>
      <div className="coverage-body">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">THIS SCAN</div>
              <h3>Analysis coverage</h3>
            </div>
          </div>
          <div className="coverage-bars">
            {[
              [
                "Class parsing",
                `${coverage.parsedClasses}/${coverage.totalClassEntries}`,
                classCoverage,
              ],
              [
                "Reachable methods",
                `${coverage.reachableMethods}/${coverage.evaluatedMethods}`,
                coverage.evaluatedMethods
                  ? Math.round(
                      (coverage.reachableMethods /
                        coverage.evaluatedMethods) *
                        1000,
                    ) / 10
                  : 0,
              ],
              [
                "Signature coverage",
                `${coverage.entriesIntegrityChecked} entries`,
                report.integrity.coveragePercent,
              ],
            ].map(([label, value, percent]) => (
              <div key={String(label)}>
                <span>
                  <strong>{label}</strong>
                  <small>{value}</small>
                </span>
                <div>
                  <i style={{ width: `${Number(percent)}%` }} />
                </div>
                <em>{Number(percent).toFixed(1)}%</em>
              </div>
            ))}
          </div>
          <div className="coverage-facts">
            <div>
              <span>Instructions decoded</span>
              <strong>{coverage.decodedInstructions.toLocaleString()}</strong>
            </div>
            <div>
              <span>Methods evaluated</span>
              <strong>{coverage.evaluatedMethods.toLocaleString()}</strong>
            </div>
            <div>
              <span>Data paths</span>
              <strong>{coverage.dataFlowTraces.toLocaleString()}</strong>
            </div>
            <div>
              <span>Payloads inspected</span>
              <strong>{coverage.payloadsInspected.toLocaleString()}</strong>
            </div>
            <div>
              <span>Truncated methods</span>
              <strong>{coverage.truncatedMethods.toLocaleString()}</strong>
            </div>
          </div>
        </section>
        <section className="panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">CORPUS SCOPE</div>
              <h3>What the benchmark proves</h3>
            </div>
          </div>
          <p className="panel-copy">{benchmark.scope}</p>
          <div className="benchmark-rules">
            <div>
              <Check size={15} />
              <span>
                <strong>Regression protection</strong>
                <small>
                  Known SilentNet, WeedHack, Fractureiser, nested archive, and
                  clean controls retain expected results.
                </small>
              </span>
            </div>
            <div>
              <Check size={15} />
              <span>
                <strong>Mutation resistance</strong>
                <small>
                  Class renames, packaging changes, encoded strings, and nested
                  delivery are exercised.
                </small>
              </span>
            </div>
            <div>
              <AlertTriangle size={15} />
              <span>
                <strong>Not a detection-rate claim</strong>
                <small>
                  Real malware can still use unseen packers, reflection,
                  native stages, or runtime-only delivery.
                </small>
              </span>
            </div>
          </div>
          {coverage.limitsHit.length > 0 && (
            <div className="coverage-limits">
              <span>LIMITS REACHED IN THIS SCAN</span>
              {coverage.limitsHit.map((limit) => (
                <code key={limit}>{limit}</code>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
